import { splitEncoding } from '@contentful/live-preview';

import type { Seo } from '@/contentful/contentful-exports';
import type { ParsedSolarByStatePath } from '@/utils/parseSolarByStatePath';

import {
  type AreaServed,
  applyGeoLocationOverrides,
  buildAreaServed,
  buildServiceLocationLabel,
  resolveGeoPageUrl,
  SUNRUN_ORGANIZATION,
} from './geoSchemaShared';

/** Editors reach for either `{location}` or `${location}`; accept both. */
const LOCATION_PLACEHOLDER = /\$?\{location\}/g;

const DEFAULT_SERVICE_NAME_TEMPLATE =
  'Residential Battery Storage and Solar Installation in {location}';

function buildServiceName(location: string, template?: string): string {
  const copy =
    splitEncoding(template || '').cleaned.trim() ||
    DEFAULT_SERVICE_NAME_TEMPLATE;

  return copy.replace(LOCATION_PLACEHOLDER, location);
}

export interface ServiceSchemaData {
  '@context': 'https://schema.org';
  '@type': 'Service';
  '@id': string;
  name: string;
  url: string;
  provider: typeof SUNRUN_ORGANIZATION;
  areaServed: AreaServed;
}

export interface BuildServiceSchemaParams {
  path: string;
  seoFields?: Seo;
  parsed: ParsedSolarByStatePath;
}

export function buildServiceSchema({
  path,
  seoFields,
  parsed,
}: BuildServiceSchemaParams): ServiceSchemaData | null {
  if (parsed.pageType !== 'state' && parsed.pageType !== 'city') {
    return null;
  }

  const resolved = applyGeoLocationOverrides(parsed, seoFields);
  const location = buildServiceLocationLabel(resolved);
  const areaServed = buildAreaServed(resolved);
  if (!location || !areaServed) {
    return null;
  }

  const url = resolveGeoPageUrl(path, seoFields);

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': url,
    name: buildServiceName(
      location,
      seoFields?.fields?.structuredDataServiceName
    ),
    url,
    provider: SUNRUN_ORGANIZATION,
    areaServed,
  };
}
